import {
    IInGameActionUIHandler,
    IInGameActionUIUpdater,
    InGameActionUI,
    ITerritoryInfo,
    troopsMoveUIDomId} from "./common";

const attackButtonId = `${troopsMoveUIDomId}-attack`;

export function showAttackUI(
    from: ITerritoryInfo,
    to: ITerritoryInfo,
    onSendAttackCallback: (numAttacks: number) => Promise<IAttackResult>,
    onCancelCallback: () => void,
): InGameActionUI {
    const handler = new AttackUIHandler(from, to, onSendAttackCallback, onCancelCallback);
    const ui = new InGameActionUI(handler, from, to);
    ui.show();
    return ui;
}

export interface IAttackResult {
    fromTroopsKilled: number;
    toTroopsKilled: number;
}

class AttackUIHandler implements IInGameActionUIHandler {

    private uiUpdater: IInGameActionUIUpdater;
    private fromTroopsKilled: number;
    private toTroopsKilled: number;
    private numAttacksQueued: number;
    private attackCallInProgress: boolean;

    public constructor(
        private readonly from: ITerritoryInfo,
        private readonly to: ITerritoryInfo,
        private readonly onSendAttack: (numAttacks: number) => Promise<IAttackResult>,
        private readonly onCancelCallback: () => void,
    ) {
        this.fromTroopsKilled = 0;
        this.toTroopsKilled = 0;
        this.numAttacksQueued = 0;
        this.attackCallInProgress = false;
    }

    public buildControlTemplate(): string {
        return "";
    }

    public buildTopActionsTemplate(): string {
        return `<button id="${attackButtonId}">Attack</button>`;
    }

    public buildBottomActionsTemplate(): string {
        return "";
    }

    public onShow(updater: IInGameActionUIUpdater): void {
        this.uiUpdater = updater;
        this.initializeEvents();
        this.updateTroopCounts();
    }

    public onCancel(): void {
        this.onCancelCallback();
    }

    public onFromTroopsClicked(): void {
        return;
    }

    public onToTroopsClicked(): void {
        return;
    }

    private initializeEvents(): void {
        this.uiUpdater.element.find(`#${attackButtonId}`).click(
            () => this.onAttackButtonClicked());
    }

    private onAttackButtonClicked(): boolean {
        if (this.from.numTroops - this.fromTroopsKilled <= 1) {
            return;
        }

        this.numAttacksQueued++;

        if (!this.attackCallInProgress) {
            this.doAttackCall();
        }

        return false; // suppress default event
    }

    private doAttackCall() {
        this.attackCallInProgress = true;
        const numAttacks = this.numAttacksQueued;
        this.numAttacksQueued = 0;

        this.onSendAttack(numAttacks).then(
            (result) => {
                if (this.uiUpdater.isDisposed()) {
                    return;
                }

                this.fromTroopsKilled += result.fromTroopsKilled;
                this.toTroopsKilled += result.toTroopsKilled;
                this.updateTroopCounts();

                if (this.from.numTroops - this.fromTroopsKilled <= 1) {
                    this.disableAttackButton();
                    return;
                }
                if (this.to.numTroops - this.toTroopsKilled <= 0) {
                    this.disableAttackButton();
                    return;
                }

                if (this.numAttacksQueued > 0) {
                    this.doAttackCall();
                } else {
                    this.attackCallInProgress = false;
                }
            },
            (reason) => {
                throw reason;
            },
        );
    }

    private disableAttackButton(): void {
        this.uiUpdater.element.find(`#${attackButtonId}`).attr("disabed", "true");
    }

    private updateTroopCounts(): void {
        this.uiUpdater.setFromTroops(this.from.numTroops - this.fromTroopsKilled);
        this.uiUpdater.setToTroops(this.to.numTroops - this.toTroopsKilled);
    }
}
